import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Accordion, Col, Container, Form, Row } from "react-bootstrap";
import { CollapsibleMenuItem } from "../../models/ui-components";
import { useAppSelector } from "../../store";
import { fetchTemplateUsage, renderTemplate } from "../../utils/http/templates";
import ActionsBottom from "./ActionsBottom";
import ActionsTop from "./ActionsTop";
import Content from "./components/Content";
import Favorites from "./components/Favorites";
import TemplateConfigParameters from "./components/TemplateConfigParameters";
import TemplateConfigTemplate from "./components/TemplateConfigTemplate";
import TemplateUsedIn from "./components/TemplateUsedIn";
import TranslationsAllLanguages from "./components/TranslationsAllLanguages";
import CreateNew from "./CreateNew";
import RenderedContent from "./RenderedContent";
import TemplateSelection from "./TemplateSelection";

function RootLayout() {
  const params = new URLSearchParams(location.search);

  const [activeTab, setActiveTab] = useState<string>(params.get("tab") || "content-body");
  const [createNew, setCreateNew] = useState(false);

  const [allKeysToTranslate, setAllKeysToTranslate] = useState<string[]>([]);
  const [specificKeysToTranslate, setSpecificKeysToTranslate] = useState<string[]>([]);

  const activeCompany = useAppSelector((state) => state.company.activeCompany);
  const selectedPath = useAppSelector((state) => state.templates.selectedPath);
  const selectedVersion = useAppSelector((state) => state.templates.selectedVersion);
  const selectedLanguage = useAppSelector((state) => state.templates.selectedLanguage);
  const selectedTemplateSummary = useAppSelector((state) => state.templates.selectedTemplateSummary);

  const { data: templateRenderReturn } = useQuery({
    queryKey: ["templateRender", selectedPath!.namespace, selectedPath!.name, selectedVersion],
    queryFn: ({ signal }) => renderTemplate({ signal, key: selectedPath!, version: selectedVersion }),
    staleTime: 2000,
  });

  const { data: templateUsedIn } = useQuery({
    queryKey: ["templateUsedIn", selectedPath!.namespace, selectedPath!.name],
    queryFn: ({ signal }) => fetchTemplateUsage({ signal, key: selectedPath! }),
    staleTime: 2000,
  });

  console.log("templateUsedIn", templateUsedIn);

  // If our template gets re(rendered), make sure we also process the translations keys
  useEffect(() => {
    if (templateRenderReturn) {
      const translations = templateRenderReturn?.template?.metadata?.translations_used || [];
      setAllKeysToTranslate(translations);
    }
  }, [templateRenderReturn]);

  // Make sure if we are on another template, we don't show the keys
  useEffect(() => {
    setSpecificKeysToTranslate([]);
  }, [selectedPath, selectedVersion]);

  // If a translation gets clicked in the render, show it in the translations tab
  const openTranslations = (keysUsed: string[]) => {
    setSpecificKeysToTranslate(keysUsed);
    setActiveTab("translations");
  };

  const isReadOnly = () => {
    return selectedVersion !== "draft" || !selectedPath!.namespace.startsWith(activeCompany?.name || "");
  };

  let dropdowns: CollapsibleMenuItem[] = [
    {
      key: "templateParams",
      label: "Template Parameters",
      component: TemplateConfigParameters,
      props: {
        path: selectedPath,
        version: selectedVersion,
        readOnly: isReadOnly(),
      },
      show: true,
    },
    {
      key: "templateConfig",
      label: "Template Configuration",
      component: TemplateConfigTemplate,
      props: {
        path: selectedPath,
        version: selectedVersion,
        readOnly: isReadOnly(),
      },
      show: true,
    },
    {
      key: "template-used-in",
      label: "Template Used In",
      component: TemplateUsedIn,
      props: {
        templateUsedIn: templateUsedIn || [],
      },
      show: Array.isArray(templateUsedIn) && templateUsedIn.length > 0,
    },
    {
      key: "content-body",
      label: "Content: Body",
      component: Content,
      props: {
        path: selectedPath,
        version: selectedVersion,
        readOnly: isReadOnly(),
      },
      show: selectedTemplateSummary != null,
    },
    {
      key: "translations",
      label: "Translations",
      component: TranslationsAllLanguages,
      props: {
        allKeysToTranslate,
        specificKeysToTranslate,
        languageOfRender: selectedLanguage,
      },
      show: allKeysToTranslate.length > 0,
    },
    // { key: "change-history", label: "Change History", component: AutomationConfigBCC },
    // { key: "variables-during-testing", label: "Variables during testing", component: AutomationConfigBCC },
  ];

  return (
    <Container>
      <TemplateSelection createNew={createNew} setCreateNew={setCreateNew} />
      {createNew && <CreateNew closeCreateNew={() => setCreateNew(false)} />}

      {!createNew && (
        <>
          <Form>
            <Row>
              <Col>
                <ActionsTop />
                <RenderedContent render={templateRenderReturn?.rendered_content} openTranslations={openTranslations} />
                <ActionsBottom />
              </Col>
              <Col>
                <Container className="automation-status p-0 mb-3">
                  <Row>
                    <Col>
                      <Favorites />
                    </Col>
                  </Row>
                </Container>
                <Accordion id="templateAccordion" defaultActiveKey={activeTab} activeKey={activeTab}>
                  {dropdowns.map((dropdown) => {
                    const Component = dropdown.component;
                    const componentProps = dropdown.props || {};

                    if (dropdown.show === false) return null;

                    return (
                      <Accordion.Item id={dropdown.key} eventKey={dropdown.key} key={dropdown.key}>
                        <Accordion.Header
                          onClick={() => {
                            setActiveTab(dropdown.key);
                          }}
                        >
                          {dropdown.label}
                        </Accordion.Header>

                        <Accordion.Body className="p-2">
                          <Component {...componentProps} />
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </Container>
  );
}

export default RootLayout;
